import JSZip from 'jszip';
import type { Invoice, InvoiceStatus } from '../types';
import { sha256Hex } from './hash';
import { performancePeriod } from './invoiceLogic';
import { formatCentsPlain } from './money';

/**
 * GoBD-Belegarchiv fuer die Betriebspruefung.
 *
 * Erzeugt ein in sich geschlossenes ZIP: die archivierten Beleg-PDFs, ein
 * maschinell auswertbares index.csv und eine audit.json mit den Metadaten je
 * Beleg. Der Pruefer soll das Archiv ohne Zugriff auf die Anwendung lesen
 * koennen — deshalb liegt jede Angabe, die zur Zuordnung noetig ist, auch im
 * Archiv selbst und nicht nur in der Datenbank.
 *
 * Der wesentliche Punkt ist die Hashpruefung: beim Packen wird jedes PDF neu
 * gehasht und gegen den beim Ausstellen gespeicherten Wert gehalten. Eine
 * Abweichung wird NICHT verschwiegen und bricht den Lauf auch nicht ab — sie
 * bekommt eine eigene Spalte in der index.csv, einen Eintrag in der audit.json
 * und steht im Rueckgabewert. Ein abgebrochener Lauf haette dem Pruefer gar
 * nichts vorzulegen; ein stillschweigend "sauberes" Archiv waere schlimmer, weil
 * es eine Unversehrtheit behauptet, die nicht geprueft wurde.
 *
 * Das Laden der PDFs und das Beschaffen der gespeicherten Hashes sind
 * hereinreichbar. Das haelt die Funktion ohne Netz und ohne Browser testbar und
 * ist zugleich die Stelle, an der ein Aufrufer eine eigene Quelle anschliessen
 * kann.
 */

/** Ablageordner der Belege im Archiv. */
const DOCUMENT_FOLDER = 'Belege';

/**
 * Deutsche Statusbezeichnung fuer index.csv und audit.json.
 *
 * Bewusst hier und nicht aus der Oberflaeche importiert: ein Service soll nicht
 * an einer Komponente haengen. Der Record-Typ erzwingt Vollstaendigkeit — kommt
 * ein Status dazu, scheitert die Uebersetzung hier und nicht erst im Archiv.
 */
const STATUS_LABEL: Record<InvoiceStatus, string> = {
    DRAFT: 'Entwurf',
    READY_FOR_REVIEW: 'In Prüfung',
    APPROVED: 'Freigegeben',
    ISSUING: 'Wird ausgestellt',
    ISSUED: 'Ausgestellt',
    SENT: 'Versendet',
    PARTIALLY_PAID: 'Teilweise bezahlt',
    PAID: 'Bezahlt',
    PARTIALLY_CREDITED: 'Teilweise gutgeschrieben',
    CREDITED: 'Gutgeschrieben',
    REFUND_PENDING: 'Erstattung offen',
    REFUNDED: 'Erstattet',
    DISPUTED: 'Strittig',
    OVERDUE: 'Überfällig',
    SUPERSEDED_BY_FINAL_INVOICE: 'Durch Schlussrechnung abgelöst',
    CANCELLED: 'Storniert',
};

export type GobdEntryStatus =
    /** PDF geladen, Hash stimmt mit dem gespeicherten ueberein. */
    | 'ok'
    /** PDF geladen, Hash weicht ab — der Beleg ist im Archiv, aber auffaellig. */
    | 'hash_mismatch'
    /** PDF geladen, es gibt aber keinen gespeicherten Hash zum Vergleich. */
    | 'hash_unknown'
    /** Der Beleg hat gar kein archiviertes PDF. */
    | 'pdf_missing'
    /** Das PDF konnte nicht geladen werden. */
    | 'load_failed';

export interface GobdArchiveEntry {
    invoiceId: string;
    invoiceNumber?: string;
    /** Dateiname im Archiv; fehlt, wenn kein PDF aufgenommen werden konnte. */
    fileName?: string;
    status: GobdEntryStatus;
    /** Beim Ausstellen gespeicherter sha256. */
    expectedSha256?: string;
    /** sha256 der tatsaechlich gepackten Bytes. */
    actualSha256?: string;
    /** Klartext fuer die Oberflaeche und die audit.json. */
    message?: string;
}

export interface GobdArchiveOptions {
    /**
     * Laedt die archivierten PDF-Bytes eines Belegs. Standard ist der Griff in
     * den Supabase-Speicher ueber eine signierte URL.
     */
    loadPdf?: (invoice: Invoice) => Promise<Uint8Array>;
    /**
     * Liefert je Beleg-ID den beim Ausstellen gespeicherten sha256. Standard ist
     * eine einzige Abfrage auf invoice_snapshots — bewusst nicht je Beleg, sonst
     * entstuende ein N+1 ueber das ganze Archiv.
     */
    loadExpectedHashes?: (invoices: Invoice[]) => Promise<Map<string, string>>;
    /** Hashfunktion. Hereingereicht, damit Tests ohne Web-Crypto laufen. */
    hashBytes?: (bytes: Uint8Array) => Promise<string>;
    /** Erzeugungszeitpunkt. Injizierbar, damit Tests feste Ergebnisse haben. */
    createdAt?: Date;
    /** Kuerzel oder Name des ausloesenden Benutzers, fuer die audit.json. */
    exportedBy?: string;
    /** Freitext, der in die audit.json wandert (z.B. der Zeitraum). */
    description?: string;
    /**
     * Exportzeitraum (jjjj-mm-tt). Steht als Vorspannzeile in der index.csv
     * und als eigene Felder in der audit.json — die Datei wird beim Pruefer
     * in Excel geoeffnet, und dort muss der Zeitraum ablesbar sein, ohne das
     * ZIP oder den Dateinamen zu bemuehen.
     */
    period?: { from: string; to: string };
    /** Fortschritt fuer die Oberflaeche. */
    onProgress?: (done: number, total: number) => void;
}

export interface GobdArchiveResult {
    fileName: string;
    /**
     * Die ZIP-Bytes. Bewusst kein Blob: als Uint8Array laesst sich das Ergebnis
     * auch ausserhalb des Browsers pruefen, und der Aufrufer wickelt es mit
     * einer Zeile ein.
     */
    bytes: Uint8Array;
    entries: GobdArchiveEntry[];
    /** Belege, deren PDF nicht mehr dem gespeicherten Hash entspricht. */
    mismatched: GobdArchiveEntry[];
    /** Belege, die es nicht ins Archiv geschafft haben. */
    incomplete: GobdArchiveEntry[];
    /** Die erzeugte index.csv als Text — fuer Anzeige und Tests. */
    indexCsv: string;
    /**
     * Probleme des Laufs, die keinen einzelnen Beleg betreffen (z.B. die
     * Hashliste liess sich gar nicht laden).
     */
    warnings: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Standardquellen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Laedt die archivierten PDF-Bytes ueber eine signierte URL.
 *
 * invoiceService wird erst hier nachgeladen, nicht am Dateikopf: dieses Modul
 * soll auch dort ladbar bleiben, wo es keinen Datenzugriff gibt — in den Tests
 * und in jedem Aufrufer, der die Bytes selbst mitbringt.
 */
export const fetchArchivedPdfBytes = async (invoice: Invoice): Promise<Uint8Array> => {
    if (!invoice.pdfUrl) {
        throw new Error(
            `Beleg ${invoice.invoiceNumber ?? invoice.id} hat kein archiviertes PDF.`,
        );
    }
    const { getInvoicePdfUrl } = await import('./invoiceService');
    const url = await getInvoicePdfUrl(invoice.pdfUrl);
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Das PDF konnte nicht geladen werden (HTTP ${response.status}).`);
    }
    return new Uint8Array(await response.arrayBuffer());
};

/** Die gespeicherten Hashes aus invoice_snapshots, in einer Abfrage. */
const loadExpectedHashesFromSnapshots = async (
    invoices: Invoice[],
): Promise<Map<string, string>> => {
    const found = new Map<string, string>();
    const ids = invoices.map((invoice) => invoice.id);
    if (ids.length === 0) return found;

    const { supabase } = await import('./supabaseClient');
    const { data, error } = await supabase
        .from('invoice_snapshots')
        .select('id, invoice_id, pdf_sha256')
        .in('invoice_id', ids);
    if (error) throw error;

    // Zu einer Rechnung kann es mehr als einen Snapshot geben (ein gescheiterter
    // Ausstellversuch hinterlaesst einen). Massgeblich ist der, auf den die
    // Rechnung zeigt — sonst wuerde gegen den Hash eines Belegs geprueft, der
    // nie ausgeliefert wurde.
    const preferred = new Map(invoices.map((invoice) => [invoice.id, invoice.snapshotId]));
    for (const row of (data ?? []) as Array<{
        id: string;
        invoice_id: string;
        pdf_sha256: string | null;
    }>) {
        if (!row.pdf_sha256) continue;
        if (!found.has(row.invoice_id) || row.id === preferred.get(row.invoice_id)) {
            found.set(row.invoice_id, row.pdf_sha256);
        }
    }
    return found;
};

// ═══════════════════════════════════════════════════════════════════════════
// Hilfsfunktionen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein CSV-Feld: gequotet, innenliegende Anfuehrungszeichen verdoppelt,
 * Zeilenumbrueche entschaerft.
 *
 * Ein fuehrendes =, +, - oder @ bekommt ein Apostroph vorangestellt. Kunden- und
 * Belegtexte sind freie Eingaben, und die index.csv wird beim Pruefer mit
 * Sicherheit in Excel geoeffnet — dort wertet ein Feld mit einem dieser
 * Anfangszeichen als Formel aus. Die Anfuehrungszeichen der CSV schuetzen davor
 * nicht.
 */
const csvField = (value: string | number | undefined): string => {
    const text = String(value ?? '').replace(/[\r\n]+/g, ' ');
    const guarded = /^[=+\-@]/.test(text) ? `'${text}` : text;
    return `"${guarded.replace(/"/g, '""')}"`;
};

const CSV_SEPARATOR = ';';
const CSV_LINE_BREAK = '\r\n';

/**
 * Byte Order Mark vor der index.csv.
 *
 * Anders als der DATEV-Buchungsstapel geht diese Datei nicht in ein
 * Buchhaltungssystem, sondern zum Pruefer — und der oeffnet sie in Excel. Ohne
 * BOM liest Excel UTF-8 als Windows-1252 und macht aus "Müller" ein "MÃ¼ller".
 */
const UTF8_BOM = '﻿';

const INDEX_COLUMNS = [
    'Nummer',
    'Datum',
    'Kunde',
    'Netto',
    'Steuer',
    'Brutto',
    'Leistung je Monat (netto)',
    'Status',
    'sha256',
    'Abweichung',
    'Datei',
] as const;

/** Dateiname im Archiv. Nur unbedenkliche Zeichen — das ZIP wird ausgepackt. */
const archiveFileName = (invoice: Invoice, taken: Set<string>): string => {
    const base =
        (invoice.invoiceNumber ?? `Beleg_${invoice.id}`).replace(/[^A-Za-z0-9._-]+/g, '_') ||
        `Beleg_${invoice.id}`;
    let candidate = `${base}.pdf`;
    // Doppelte Nummern darf es nicht geben, ein Archiv mit einer stillschweigend
    // ueberschriebenen Datei aber erst recht nicht.
    let counter = 2;
    while (taken.has(candidate)) {
        candidate = `${base}_${counter}.pdf`;
        counter += 1;
    }
    taken.add(candidate);
    return candidate;
};

const customerNameOf = (invoice: Invoice): string =>
    invoice.customerSnapshot?.name ?? invoice.customerName ?? '';

/**
 * Aufschluesselung des Belegnettos nach LEISTUNGSmonat, z.B.
 * "05/2026: 640,00 | 06/2026: 800,00".
 *
 * Quelle sind die beim Ausstellen festgeschriebenen allocations der
 * Rechnungszeilen — sie tragen das Leistungsdatum je abgerechnetem
 * Protokolleintrag. Das Belegdatum sagt nur, WANN gebucht wird; diese Spalte
 * sagt, aus welchen Monaten die Summe stammt (4 Begehungen Mai + 5 Juni).
 *
 * Zeilenbetraege werden anteilig nach Menge auf die Monate verteilt und je
 * Monat gerundet — die Summe kann deshalb um Cent vom Zeilennetto abweichen;
 * die Spalte ist Auskunft, keine Buchungsgrundlage. Belege ohne allocations
 * (Altbelege, Abschlaege, Freitextzeilen) fallen auf den Leistungszeitraum
 * des Belegs zurueck; ohne beides bleibt die Zelle leer.
 */
const serviceMonthBreakdown = (invoice: Invoice): string => {
    const byMonth = new Map<string, number>();
    let undatedCents = 0;

    for (const line of invoice.lines ?? []) {
        if (line.kind !== 'SERVICE') continue;
        const allocations = line.allocations ?? [];
        if (allocations.length === 0) continue;
        const totalQty = allocations.reduce((sum, a) => sum + Math.abs(a.quantity), 0);
        if (totalQty <= 0) continue;
        for (const allocation of allocations) {
            const share = line.netCents * (Math.abs(allocation.quantity) / totalQty);
            const month = allocation.date?.slice(0, 7);
            if (month) byMonth.set(month, (byMonth.get(month) ?? 0) + share);
            else undatedCents += share;
        }
    }

    if (byMonth.size === 0 && undatedCents === 0) {
        // Der LEISTUNGSZEITRAUM, den der Beleg ausweist — die Spalte fragt nach
        // Leistungsmonaten, nicht danach, worueber abgerechnet wurde. Der
        // Pruefer haelt die Zeile neben das PDF, und sie muss dasselbe sagen wie
        // das Blatt. Die rohen Feldwerte stehen unveraendert in der audit.json.
        const performed = performancePeriod(invoice);
        if (performed.from && performed.to) {
            return performed.from === performed.to
                ? `Leistungsdatum ${performed.to}`
                : `Leistungszeitraum ${performed.from} bis ${performed.to}`;
        }
        // Nur eine Seite ist ein Datum, kein halber Zeitraum — und vor allem
        // kein Grund, die Angabe ganz wegzulassen. Frueher blieb die Zelle in
        // genau diesem Fall leer, und der Pruefer sah zu einem Beleg mit
        // Leistungsdatum eine Spalte ohne jede Auskunft.
        if (performed.to) return `Leistungsdatum ${performed.to}`;
        if (performed.from) return `Leistung ab ${performed.from}`;
        return '';
    }

    const monthLabel = (key: string): string => `${key.slice(5, 7)}/${key.slice(0, 4)}`;
    const parts = [...byMonth.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, cents]) => `${monthLabel(month)}: ${formatCentsPlain(Math.round(cents))}`);
    if (undatedCents > 0) parts.push(`ohne Datum: ${formatCentsPlain(Math.round(undatedCents))}`);
    return parts.join(' | ');
};

/** Klartext zur Abweichungsspalte. Leer heisst: geprueft und in Ordnung. */
const deviationText = (entry: GobdArchiveEntry): string => {
    switch (entry.status) {
        case 'ok':
            return '';
        case 'hash_mismatch':
            return `Hash weicht ab — tatsächlich ${entry.actualSha256 ?? '—'}`;
        case 'hash_unknown':
            return 'Kein gespeicherter Hash vorhanden — Unversehrtheit nicht prüfbar';
        case 'pdf_missing':
            return 'Kein archiviertes PDF vorhanden';
        case 'load_failed':
            return `PDF konnte nicht geladen werden: ${entry.message ?? 'unbekannter Fehler'}`;
    }
};

const pad = (value: number): string => String(value).padStart(2, '0');

/** JJJJ-MM-TT aus dem lokalen Datum, ohne Umweg ueber die Zeitzone von toISOString. */
const isoDay = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// ═══════════════════════════════════════════════════════════════════════════
// Oeffentliche Schnittstelle
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Packt die uebergebenen Belege in ein GoBD-Archiv.
 *
 * Der Aufrufer entscheidet, welche Belege hineingehoeren — hier wird nicht
 * gefiltert. Was kein PDF hat oder sich nicht laden laesst, faellt nicht
 * heraus, sondern bekommt eine Zeile mit Begruendung.
 */
export const buildGobdArchive = async (
    invoices: Invoice[],
    options: GobdArchiveOptions = {},
): Promise<GobdArchiveResult> => {
    const createdAt = options.createdAt ?? new Date();
    const loadPdf = options.loadPdf ?? fetchArchivedPdfBytes;
    const hashBytes = options.hashBytes ?? sha256Hex;
    const warnings: string[] = [];

    let expectedHashes = new Map<string, string>();
    try {
        expectedHashes = await (options.loadExpectedHashes ?? loadExpectedHashesFromSnapshots)(
            invoices,
        );
    } catch (error) {
        // Ohne Vergleichswerte wird trotzdem gepackt — nur eben ohne
        // Unversehrtheitsnachweis, und das steht dann so im Archiv.
        warnings.push(
            'Die gespeicherten Prüfsummen konnten nicht geladen werden: ' +
                `${error instanceof Error ? error.message : String(error)}. ` +
                'Die Belege sind im Archiv, ihre Unversehrtheit ist aber ungeprüft.',
        );
    }

    const zip = new JSZip();
    const folder = zip.folder(DOCUMENT_FOLDER);
    const entries: GobdArchiveEntry[] = [];
    const takenNames = new Set<string>();

    for (const [index, invoice] of invoices.entries()) {
        const expectedSha256 = expectedHashes.get(invoice.id);
        const entry: GobdArchiveEntry = {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            status: 'ok',
            expectedSha256,
        };

        if (!invoice.pdfUrl) {
            entry.status = 'pdf_missing';
            entry.message =
                'Zu diesem Beleg ist kein PDF archiviert. Er ist in der Liste aufgeführt, liegt dem Archiv aber nicht bei.';
        } else {
            try {
                const bytes = await loadPdf(invoice);
                const actualSha256 = await hashBytes(bytes);
                entry.actualSha256 = actualSha256;
                entry.fileName = archiveFileName(invoice, takenNames);

                if (!expectedSha256) {
                    entry.status = 'hash_unknown';
                } else if (expectedSha256.toLowerCase() !== actualSha256.toLowerCase()) {
                    entry.status = 'hash_mismatch';
                    entry.message =
                        `Erwartet ${expectedSha256}, tatsächlich ${actualSha256}. ` +
                        'Die archivierte Datei entspricht nicht mehr dem beim Ausstellen festgehaltenen Stand.';
                }

                // Festes Datum an jedem Eintrag: sonst traegt jeder Lauf den
                // Zeitpunkt des Packens und zwei Archive desselben Bestands
                // unterscheiden sich Byte fuer Byte.
                folder?.file(entry.fileName, bytes, { date: createdAt });
            } catch (error) {
                entry.status = 'load_failed';
                entry.message = error instanceof Error ? error.message : String(error);
            }
        }

        entries.push(entry);
        options.onProgress?.(index + 1, invoices.length);
    }

    const byId = new Map(entries.map((entry) => [entry.invoiceId, entry]));

    // Vorspann mit dem Exportzeitraum, dann eine Leerzeile, dann die Tabelle.
    // Excel zeigt die Vorspannzeile als normale Zellen ueber den Spaltenkoepfen.
    const periodPreamble = options.period
        ? [
              [
                  csvField('Zeitraum von'),
                  csvField(options.period.from),
                  csvField('bis'),
                  csvField(options.period.to),
              ].join(CSV_SEPARATOR),
              '',
          ]
        : [];

    const indexCsv =
        UTF8_BOM +
        [
            ...periodPreamble,
            INDEX_COLUMNS.map(csvField).join(CSV_SEPARATOR),
            ...invoices.map((invoice) => {
                const entry = byId.get(invoice.id);
                return [
                    csvField(invoice.invoiceNumber ?? ''),
                    csvField(invoice.invoiceDate),
                    csvField(customerNameOf(invoice)),
                    csvField(formatCentsPlain(invoice.totals.netTotalCents)),
                    csvField(formatCentsPlain(invoice.totals.taxTotalCents)),
                    csvField(formatCentsPlain(invoice.totals.grossTotalCents)),
                    csvField(serviceMonthBreakdown(invoice)),
                    csvField(STATUS_LABEL[invoice.status] ?? invoice.status),
                    csvField(entry?.expectedSha256 ?? ''),
                    csvField(entry ? deviationText(entry) : ''),
                    csvField(entry?.fileName ?? ''),
                ].join(CSV_SEPARATOR);
            }),
        ].join(CSV_LINE_BREAK) +
        CSV_LINE_BREAK;

    const mismatched = entries.filter((entry) => entry.status === 'hash_mismatch');
    const incomplete = entries.filter(
        (entry) => entry.status === 'pdf_missing' || entry.status === 'load_failed',
    );

    // Deutsche Schluessel: die audit.json ist kein Schnittstellenformat, sondern
    // ein Dokument fuer die Betriebspruefung — wie die Spaltenkoepfe der
    // index.csv auch.
    const audit = {
        archiv: 'GoBD-Belegarchiv',
        erstelltAm: createdAt.toISOString(),
        erstelltVon: options.exportedBy ?? '',
        bezeichnung: options.description ?? '',
        zeitraumVon: options.period?.from ?? '',
        zeitraumBis: options.period?.to ?? '',
        belegeGesamt: invoices.length,
        belegeImArchiv: entries.filter((entry) => entry.fileName).length,
        hashAbweichungen: mismatched.length,
        hashUngeprueft: entries.filter((entry) => entry.status === 'hash_unknown').length,
        nichtEnthalten: incomplete.length,
        hinweise: [
            'Die Prüfsumme jeder Datei wurde beim Packen neu berechnet und gegen den beim Ausstellen gespeicherten Wert gehalten.',
            'Abweichungen und fehlende Belege sind in der Spalte "Abweichung" der index.csv sowie im Feld "hashStatus" dieser Datei ausgewiesen.',
            ...warnings,
        ],
        belege: invoices.map((invoice) => {
            const entry = byId.get(invoice.id);
            return {
                rechnungsnummer: invoice.invoiceNumber ?? '',
                belegart: invoice.documentType,
                status: STATUS_LABEL[invoice.status] ?? invoice.status,
                belegdatum: invoice.invoiceDate,
                leistungszeitraum: {
                    von: invoice.deliveryDateFrom ?? '',
                    bis: invoice.deliveryDateTo ?? '',
                },
                faelligkeit: invoice.dueDate,
                kunde: {
                    name: customerNameOf(invoice),
                    kundennummer: invoice.customerSnapshot?.customerNumber ?? '',
                },
                betraege: {
                    nettoCent: invoice.totals.netTotalCents,
                    steuerCent: invoice.totals.taxTotalCents,
                    bruttoCent: invoice.totals.grossTotalCents,
                    // Der Sicherheitseinbehalt gehoert ins Protokoll, obwohl er
                    // im Zahlbetrag schon steckt: aus Brutto und Zahlbetrag
                    // allein liesse sich nicht unterscheiden, ob die Differenz
                    // aus Abschlagsabzug, Einbehalt oder Auszahlung stammt. Der
                    // Pruefer muss den Zahlbetrag nachrechnen koennen.
                    //
                    // ?? 0 wie ueberall: beide Felder sind optional, und fuer
                    // jeden Beleg aus der Zeit vor dem Einbehalt ist 0 die
                    // Wahrheit — nicht "unbekannt".
                    sicherheitseinbehaltCent: invoice.totals.retentionCents ?? 0,
                    einbehaltAuszahlungCent: invoice.totals.retentionReleaseCents ?? 0,
                    zahlbetragCent: invoice.totals.payableCents,
                },
                ausgestelltAm: invoice.issuedAt ?? '',
                ausgestelltVon: invoice.issuedBy ?? '',
                nachDatevExportiertAm: invoice.exportedAt ?? '',
                archivpfad: invoice.pdfUrl ?? '',
                datei: entry?.fileName ? `${DOCUMENT_FOLDER}/${entry.fileName}` : '',
                hashStatus: entry?.status ?? 'pdf_missing',
                hashGespeichert: entry?.expectedSha256 ?? '',
                hashGeprueft: entry?.actualSha256 ?? '',
                anmerkung: entry?.message ?? '',
            };
        }),
    };

    zip.file('index.csv', indexCsv, { date: createdAt });
    zip.file('audit.json', `${JSON.stringify(audit, null, 4)}\n`, { date: createdAt });

    const bytes = await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });

    return {
        fileName: `GoBD-Archiv_${isoDay(createdAt)}.zip`,
        bytes,
        entries,
        mismatched,
        incomplete,
        indexCsv,
        warnings,
    };
};
